import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Kinematics",
  examWeight: "10–15%",
  focus: "How do objects move in one and two dimensions, and how can that motion be modeled mathematically and graphically?",
  sections: [
    {
      id: "overview",
      title: "Unit Overview",
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            Kinematics is the study of <strong>how</strong> objects move, without regarding the forces that cause the motion.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="font-bold text-slate-900 flex items-center gap-2">
              ⚛️ The Particle Model
            </h4>
            <p className="text-sm text-slate-700 mt-1">
              Also known as the <strong>Object Model</strong>. We assume an object can be treated as a single point with all its mass concentrated at that point, ignoring internal structure and rotation for this unit.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "topic-1-1",
      title: "Topic 1.1: Scalars and Vectors in One Dimension",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Definitions and Differences</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-blue-800">📏 Scalars</h4>
                <p className="text-sm text-slate-600 mb-2">Described by <strong>magnitude</strong> (size) only.</p>
                <ul className="text-xs space-y-1 text-slate-700 list-disc pl-4">
                  <li>Time (<InlineMath math="t" />)</li>
                  <li>Mass (<InlineMath math="m" />)</li>
                  <li>Distance (<InlineMath math="d" />)</li>
                  <li>Speed (<InlineMath math="s" />)</li>
                  <li>Temperature (<InlineMath math="T" />)</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-purple-800">🏹 Vectors</h4>
                <p className="text-sm text-slate-600 mb-2">Described by <strong>magnitude AND direction</strong>.</p>
                <ul className="text-xs space-y-1 text-slate-700 list-disc pl-4">
                  <li>Displacement (<InlineMath math="\Delta x" />)</li>
                  <li>Velocity (<InlineMath math="v" />)</li>
                  <li>Acceleration (<InlineMath math="a" />)</li>
                  <li>Force (<InlineMath math="F" />)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Direction in One Dimension (1D)</h3>
            <p className="text-slate-700 text-sm mb-3">Vectors are represented algebraically using signs (+ or -):</p>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-green-50 p-3 rounded border border-green-200 text-center">
                <span className="font-bold text-green-800 text-sm">Positive (+):</span>
                <p className="text-xs text-slate-600">Right, Up, East, North</p>
              </div>
              <div className="flex-1 bg-red-50 p-3 rounded border border-red-200 text-center">
                <span className="font-bold text-red-800 text-sm">Negative (-):</span>
                <p className="text-xs text-slate-600">Left, Down, West, South</p>
              </div>
            </div>
            <ImportantNote>
              You define the coordinate system. You can choose "down" to be positive as long as you are consistent.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Distance vs. Displacement</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Distance (d)" 
                definition="Scalar. The total path length traveled. Always positive. 'How much ground you covered.'"
              />
              <KeyTerm 
                term="Displacement (Δx)" 
                definition="Vector. The change in position. 'How far you are from where you started.'"
              />
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="text-sm font-bold text-slate-800 mb-2">Displacement Equation:</h4>
                <BlockMath math="\Delta x = x_f - x_i" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Displacement, Velocity, and Acceleration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Speed vs. Velocity</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-bold text-blue-900 text-sm">Average Speed</h4>
                <p className="text-xs text-slate-700 mt-1">Rate at which distance is covered.</p>
                <BlockMath math="\text{Avg Speed} = \frac{\text{Total Distance}}{\text{Total Time}}" />
              </div>
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <h4 className="font-bold text-indigo-900 text-sm">Average Velocity (v̄)</h4>
                <p className="text-xs text-slate-700 mt-1">Rate at which position changes.</p>
                <BlockMath math="\bar{v} = \frac{\Delta x}{\Delta t} = \frac{x_f - x_i}{t_f - t_i}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Acceleration (a)</h3>
            <p className="text-slate-700 text-sm mb-4">The rate at which <strong>velocity</strong> changes. Acceleration occurs if speed changes, direction changes, or both.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\bar{a} = \frac{\Delta v}{\Delta t} = \frac{v_f - v_i}{t_f - t_i}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. The Speeding Up/Slowing Down Rule</h3>
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">▶</span>
                  <p className="text-sm text-slate-700"><strong>Speeding Up:</strong> Velocity and Acceleration have the <strong>SAME sign</strong> (both + or both -).</p>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">▶</span>
                  <p className="text-sm text-slate-700"><strong>Slowing Down:</strong> Velocity and Acceleration have <strong>OPPOSITE signs</strong>.</p>
                </li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Representing Motion (Graphs & Equations)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Motion Graphs</h3>
            <div className="space-y-4">
              <div className="p-4 border-l-4 border-blue-400 bg-slate-50 rounded-r-lg">
                <h4 className="font-bold text-blue-900">A. Position vs. Time (x vs. t)</h4>
                <ul className="text-sm text-slate-700 list-disc pl-5 mt-2 space-y-1">
                  <li><strong>y-axis value:</strong> Current position.</li>
                  <li><strong>Slope:</strong> Velocity (<InlineMath math="v" />).</li>
                  <li><strong>Curvature:</strong> Acceleration (<InlineMath math="a" />). Happy face (Concave Up) = <InlineMath math="+a" />, Frown face (Concave Down) = <InlineMath math="-a" />.</li>
                </ul>
              </div>
              <div className="p-4 border-l-4 border-green-400 bg-slate-50 rounded-r-lg">
                <h4 className="font-bold text-green-900">B. Velocity vs. Time (v vs. t)</h4>
                <ul className="text-sm text-slate-700 list-disc pl-5 mt-2 space-y-1">
                  <li><strong>y-axis value:</strong> Instantaneous velocity.</li>
                  <li><strong>Slope:</strong> Acceleration (<InlineMath math="a" />).</li>
                  <li><strong>Area Under Curve:</strong> Displacement (<InlineMath math="\Delta x" />).</li>
                  <li><strong>Crossing t-axis:</strong> Object changed direction.</li>
                </ul>
              </div>
              <div className="p-4 border-l-4 border-purple-400 bg-slate-50 rounded-r-lg">
                <h4 className="font-bold text-purple-900">C. Acceleration vs. Time (a vs. t)</h4>
                <ul className="text-sm text-slate-700 list-disc pl-5 mt-2 space-y-1">
                  <li><strong>Area Under Curve:</strong> Change in Velocity (<InlineMath math="\Delta v" />).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The "Big Three" Kinematic Equations</h3>
            <ImportantNote>
              These only work when <strong>acceleration is constant</strong>.
            </ImportantNote>
            <div className="grid gap-3 mt-4">
              <div className="p-4 bg-slate-900 text-slate-100 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-mono text-blue-400">Velocity Function (No Δx)</span>
                </div>
                <BlockMath math="v_x = v_{x0} + a_x t" />
              </div>
              <div className="p-4 bg-slate-900 text-slate-100 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-mono text-green-400">Displacement Function (No v_f)</span>
                </div>
                <BlockMath math="x = x_0 + v_{x0}t + \frac{1}{2}a_x t^2" />
              </div>
              <div className="p-4 bg-slate-900 text-slate-100 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-mono text-purple-400">Time-Independent (No t)</span>
                </div>
                <BlockMath math="v_x^2 = v_{x0}^2 + 2a_x(x - x_0)" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Free Fall</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <ul className="text-sm text-slate-700 space-y-2">
                <li><strong>Gravity (g):</strong> On Earth, <InlineMath math="g \approx 9.8 \, \text{m/s}^2" />. Use <InlineMath math="10 \, \text{m/s}^2" /> for AP calculations.</li>
                <li><strong>Direction:</strong> Always points <strong>down</strong>. If "up" is positive, <InlineMath math="a = -10 \, \text{m/s}^2" />.</li>
                <li><strong>Apex:</strong> At the top of a path, vertical velocity is <strong>zero</strong>, but acceleration is still <InlineMath math="-9.8 \, \text{m/s}^2" />.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Reference Frames and Relative Motion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Inertial Reference Frames</h3>
            <p className="text-sm text-slate-700 leading-relaxed mb-4">
              Motion is relative to the observer. An <strong>Inertial Reference Frame</strong> moves at a constant velocity. Newton's Laws hold true in these frames.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Relative Velocity Addition</h3>
            <p className="text-sm text-slate-700 mb-2">To find Object A relative to C, link them through common frame B:</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\vec{v}_{AC} = \vec{v}_{AB} + \vec{v}_{BC}" />
              <div className="mt-4 p-3 bg-white rounded border border-slate-200">
                <h5 className="font-bold text-xs text-slate-800">The Subscript Rule:</h5>
                <p className="text-xs text-slate-600">The inner subscripts (B) must match for the vector addition to be valid.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Vectors and Motion in Two Dimensions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Independence of Motion</h3>
            <div className="p-4 bg-indigo-900 text-white rounded-lg shadow-lg">
              <p className="font-bold text-indigo-300 uppercase tracking-wider text-xs mb-2">Core Principle</p>
              <p className="text-sm italic">"Motion in the x-direction is independent of motion in the y-direction."</p>
              <p className="text-xs mt-3 text-indigo-200">The only shared variable is <strong>Time (t)</strong>.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Vector Decomposition</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded border border-slate-200">
                <BlockMath math="V_x = V \cos(\theta)" />
              </div>
              <div className="p-3 bg-slate-50 rounded border border-slate-200">
                <BlockMath math="V_y = V \sin(\theta)" />
              </div>
              <div className="p-3 bg-slate-50 rounded border border-slate-200">
                <BlockMath math="V = \sqrt{V_x^2 + V_y^2}" />
              </div>
              <div className="p-3 bg-slate-50 rounded border border-slate-200">
                <BlockMath math="\theta = \tan^{-1}(V_y / V_x)" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Projectile Motion Analysis</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Horizontal Axis (x)</h4>
                <ul className="text-xs space-y-2 mt-2 text-slate-700">
                  <li><strong>Acceleration:</strong> <InlineMath math="a_x = 0" /></li>
                  <li><strong>Velocity:</strong> Constant (<InlineMath math="v_x = v_{x0}" />)</li>
                  <li><strong>Equation:</strong> <InlineMath math="\Delta x = v_x t" /></li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Vertical Axis (y)</h4>
                <ul className="text-xs space-y-2 mt-2 text-slate-700">
                  <li><strong>Acceleration:</strong> <InlineMath math="a_y = -g \approx -10 \, \text{m/s}^2" /></li>
                  <li><strong>Velocity:</strong> Changes by 10 m/s every second</li>
                  <li><strong>Equations:</strong> Use the "Big Three"</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Problem Solving Types</h3>
            <div className="space-y-3">
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-slate-800 text-sm">Type 1: Horizontal Launch</h4>
                <p className="text-xs text-slate-600 mt-1">Initial vertical velocity (<InlineMath math="v_{y0}" />) is zero. Time to fall: <InlineMath math="t = \sqrt{2h/g}" />.</p>
              </div>
              <div className="p-4 bg-white border border-slate-200 rounded-lg">
                <h4 className="font-bold text-slate-800 text-sm">Type 2: Angled Launch</h4>
                <p className="text-xs text-slate-600 mt-1">Must decompose <InlineMath math="v_0" />. At peak, <InlineMath math="v_y = 0" /> but <InlineMath math="v_x \neq 0" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏁 AP Exam Tips</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
              <div>
                <h4 className="font-bold text-sm text-slate-900">1. Justify your answer</h4>
                <p className="text-xs text-slate-700">Use Claim-Evidence-Reasoning. Don't just cite equations; explain the physical principle (e.g., "Since slope is acceleration...").</p>
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">2. Representational Translation</h4>
                <p className="text-xs text-slate-700">Be ready to move from a word story to a graph or from a graph to an equation.</p>
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">3. Center of Mass</h4>
                <p className="text-xs text-slate-700">For complex/rotating objects, kinematic equations apply to the <strong>Center of Mass</strong>, which traces a perfect parabola.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default physicsUnit1Notes;